import { useEffect, useRef } from "react";
import { Train, Bus, Car, MapPin } from "lucide-react";
import { useLang } from "../LanguageContext";

const iconMap = { Train, Bus, Car };

export default function UbicacionSection() {
  const { t } = useLang();
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("fade-in-visible")),
      { threshold: 0.1 }
    );
    const els = ref.current?.querySelectorAll(".fade-in-item");
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="ubicacion"
      ref={ref}
      data-testid="ubicacion-section"
      className="bg-[#E8DCC8] py-24 md:py-36"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-12">
          <p
            className="fade-in-item text-xs uppercase tracking-[0.4em] text-[#C8A96E] mb-4"
            style={{ fontFamily: "Lato, sans-serif" }}
          >
            {t.ubicacion.caption}
          </p>
          <div className="fade-in-item w-12 h-px bg-[#C8A96E] mb-8" />
          <h2
            className="fade-in-item text-4xl md:text-5xl font-light text-[#1A1A18] leading-tight"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            {t.ubicacion.heading}
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Map */}
          <div className="fade-in-item">
            <div className="w-full h-[400px] overflow-hidden border border-[#C8A96E]/20">
              <iframe
                data-testid="google-maps-embed"
                title="Torre Amat location"
                width="100%"
                height="100%"
                style={{ border: 0, filter: "sepia(0.2) contrast(0.95)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2993.1234567890123!2d2.1180833!3d41.3975213!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12a498414dabd4cd%3A0xb4c919e11768725e!2sTorre%20Amat.%20Sarri%C3%A0!5e0!3m2!1ses!2ses!4v1740000000000!5m2!1ses!2ses"
              />
            </div>
          </div>

          {/* Info */}
          <div className="fade-in-item">
            {/* Address */}
            <div className="flex items-start gap-4 mb-8 pb-8 border-b border-[#C8A96E]/20">
              <div className="w-10 h-10 flex items-center justify-center border border-[#C8A96E]/40 flex-shrink-0">
                <MapPin size={18} className="text-[#C8A96E]" strokeWidth={1.5} />
              </div>
              <div>
                <p
                  className="text-sm font-medium text-[#1A1A18] mb-1"
                  style={{ fontFamily: "'Libre Baskerville', serif" }}
                >
                  Torre Amat
                </p>
                <p
                  className="text-sm text-[#1A1A18]/65"
                  style={{ fontFamily: "Lato, sans-serif" }}
                >
                  {t.ubicacion.address}
                </p>
              </div>
            </div>

            {/* Transport */}
            <h3
              className="text-lg font-light text-[#1A1A18] mb-6"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              {t.ubicacion.transport}
            </h3>
            <div className="space-y-5">
              {t.ubicacion.transport_items.map((item, i) => {
                const Icon = iconMap[item.icon] || Train;
                const isParking = item.icon === "Car";
                return (
                  <div
                    key={i}
                    id={isParking ? "parking-info" : undefined}
                    data-testid={`transport-item-${i}`}
                    className={`flex items-start gap-4 ${
                      isParking ? "scroll-mt-28 -m-2 p-2 rounded-sm transition-colors" : ""
                    }`}
                  >
                    <div className="w-8 h-8 flex items-center justify-center border border-[#C8A96E]/30 flex-shrink-0">
                      <Icon size={14} className="text-[#C8A96E]" strokeWidth={1.5} />
                    </div>
                    <div>
                      <span
                        className="text-xs uppercase tracking-[0.2em] text-[#1A1A18] font-medium"
                        style={{ fontFamily: "Lato, sans-serif" }}
                      >
                        {item.label}
                      </span>
                      <p
                        className="text-sm text-[#1A1A18]/60 mt-0.5"
                        style={{ fontFamily: "Lato, sans-serif" }}
                      >
                        {item.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Private note */}
            <div className="mt-10 p-5 border-l-2 border-[#C8A96E] bg-[#F5F0E8]">
              <p
                className="text-sm text-[#1A1A18]/70 italic"
                style={{ fontFamily: "'Libre Baskerville', serif" }}
              >
                {t.ubicacion.private_note}
              </p>
            </div>

            {/* Review button */}
            <div className="mt-8">
              <a
                data-testid="google-review-btn"
                href="https://www.google.com/maps/place/Torre+Amat.+Sarri%C3%A0/@41.3975213,2.1180833,17z/data=!4m8!3m7!1s0x12a498414dabd4cd:0xb4c919e11768725e!8m2!3d41.3975173!4d2.1206582!9m1!1b1!16s%2Fg%2F1thlq309?entry=ttu&g_ep=EgoyMDI2MDQwOC4wIKXMDSoASAFQAw%3D%3D"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 border border-[#C8A96E] text-[#1A1A18] text-xs uppercase tracking-[0.2em] hover:bg-[#C8A96E] hover:text-[#F5F0E8] transition-all duration-300"
                style={{ fontFamily: "Lato, sans-serif" }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/></svg>
                Dejar una reseña
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
